import CustomModal from '../reusable/CustomModal';
import CustomButton from '../reusable/CustomButton';
import SuccessIcon from '../assets/data-upload-success.svg';
import XMarkIcon from '../assets/data-upload-x-mark.svg';
import WarningIcon from '../assets/data-upload-warning-amber.svg';
import { FileUploadResult, UploadedFileData } from '../../interfaces/UploadedFileDataInterface';
import CustomDivider from '../reusable/CustomDivider';
import FileTypesValues from '../../interfaces/FileType';
import useDemographicCategories from '../hooks/useDemographicCategories';
import useSchools from '../hooks/useSchools';
import ExternalSchoolTag from '../reusable/ExternalSchoolTag';
import { fileTypes } from '../../constants';

import './UploadResultsModal.css';

type FileTypes =
  | 'STUDENT_ROSTER'
  | 'COURSE_CATALOG'
  | 'STUDENT_DEMOGRAPHICS'
  | 'TRANSCRIPT'
  | 'STUDENT_TEST_SCORES'
  | 'CTE_PATHWAY_CATALOG';

const headerMapping: Record<FileTypes, Record<string, string>> = {
  CTE_PATHWAY_CATALOG: {
    academic_periods: 'Academic Years',
    schools: 'Schools',
  },
  COURSE_CATALOG: {
    academic_periods: 'Academic Years',
    schools: 'Schools',
  },
  TRANSCRIPT: {
    schools: 'Schools',
  },
  STUDENT_ROSTER: {
    schools: 'Schools',
  },
  STUDENT_DEMOGRAPHICS: {
    demographic_types: 'Demographic type',
    schools: 'Schools',
  },
  STUDENT_TEST_SCORES: {
    test_types: 'Test type',
    schools: 'Schools',
  },
};

interface UploadResultsModalProps {
  fileType: FileTypesValues;
  handleCloseModal: () => void;
  handleResubmit: () => void;
  results: FileUploadResult[];
  missingColumns: string[];
  statistics: string[];
  isViewOnly: boolean;
  fileMetadata: UploadedFileData['user_provided_file_metadata'];
}

function UploadResultsModal({
  fileType,
  handleCloseModal,
  handleResubmit,
  results,
  missingColumns,
  statistics,
  fileMetadata,
  isViewOnly,
}: UploadResultsModalProps) {
  const {
    demographicCategories: { data: demographicCategories },
  } = useDemographicCategories();
  const {
    schools: { data: schools },
  } = useSchools();

  const getOrderedSelectedItems = (key: string, values: (string | { id: string; name: string })[]): string[] => {
    if (key === 'schools' && schools) {
      const filteredSchools =
        fileType === fileTypes.COURSE_CATALOG
          ? schools
          : schools.filter((school) => school.is_primary_on_eoa !== false);

      return filteredSchools
        .filter((school) => (values as { id: string }[]).map(({ id }) => id).includes(school.id))
        .map((school) => school.name);
    } else if (key === 'demographic_types' && demographicCategories) {
      return demographicCategories
        .filter((category) => values.includes(category.name))
        .map((category) => category.display_name);
    } else {
      return values.map((value) => (typeof value === 'object' ? value.name : value));
    }
  };

  const isExternalSchool = (schoolName: string): boolean => {
    const school = schools?.find((s) => s.name === schoolName);
    return school?.is_primary_on_eoa === false;
  };

  const renderStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return SuccessIcon;
      case 'failure':
        return XMarkIcon;
      default:
        return WarningIcon;
    }
  };

  return (
    <CustomModal title="Upload results" isOpen={true} handleClose={handleCloseModal} icon={false}>
      <div className="upload-results-container">
        {statistics && statistics.length > 0 && (
          <>
            <p className="upload-results-title">File overview</p>
            <ul className="upload-results-list">
              {statistics.map((statistic, index) => (
                <li key={index}>{statistic}</li>
              ))}
            </ul>
          </>
        )}
        {((results && results.length > 0) || (missingColumns && missingColumns.length > 0)) && (
          <p className="upload-results-title">Data matching</p>
        )}
        {results && results.length > 0 && (
          <>
            {results.map((result, index) => (
              <div key={index} className={`result-container ${result.status}`}>
                <div className="result-status">
                  <img className="icon-size" src={renderStatusIcon(result.status)} alt="data matching status icon" />
                  <p>{result.description}</p>
                </div>
              </div>
            ))}
          </>
        )}
        {missingColumns && missingColumns.length > 0 && (
          <div className="result-container missing-columns">
            <div className="result-status">
              <img className="icon-size" src={renderStatusIcon('failure')} alt="data matching status icon" />
              <p className="missing-columns-title">
                The following columns were <span>NOT</span> found in this file:
              </p>
            </div>
            <ul className="upload-results-list">
              {missingColumns.map((column, idx) => (
                <li key={idx}>{column}</li>
              ))}
            </ul>
          </div>
        )}
        <div className="files-metadata-section">
          <p>Your selections</p>
          <div className="files-metadata">
            {/* sort the entries so that schools are always last in order */}
            {Object.entries(fileMetadata)
              .sort((metaData1) => (metaData1[0] === 'schools' ? 1 : -1))
              .map(([key, values]) => {
                const header = headerMapping[fileType as keyof typeof headerMapping][key];

                return header ? (
                  <div key={key} className="meta-data-container">
                    <h4>{header}</h4>
                    <div className="file-metadata">
                      {values &&
                        getOrderedSelectedItems(key, values).map((val, index) => (
                          <p key={index} className="file-metadata-item">
                            <span className="file-metadata-value">
                              {val}
                              {key === 'schools' && isExternalSchool(val) && <ExternalSchoolTag variant="ext" />}
                            </span>
                          </p>
                        ))}
                    </div>
                  </div>
                ) : null;
              })}
          </div>
        </div>
        <CustomDivider size="medium" orientation="horizontal" />
        {!isViewOnly && (
          <div className="custom-modal-actions-container">
            <CustomButton variant="secondary" buttonText="Replace" buttonAction={handleResubmit} />
            <CustomButton variant="primary" buttonText="Continue" buttonAction={handleCloseModal} />
          </div>
        )}
      </div>
    </CustomModal>
  );
}

export default UploadResultsModal;
